import type { IDropdownOption, IObjectWithKey } from '@fluentui/react';
import type { AgentTaskPullRequestRepairProgress } from '../../../../../Contracts/agent-task-manager-typescript/src/api-types.gen';

export type { AgentTaskPullRequestRepairProgress } from '../../../../../Contracts/agent-task-manager-typescript/src/api-types.gen';
export type { AutoCompleteAgentTaskPullRequestsResult } from '../../../../../Contracts/agent-task-manager-typescript/src/api-types.gen';

export interface AgentTaskListCollectionResult {
    isSynthetic: boolean;
    taskLists: AgentTaskListSummary[];
}

export interface AgentTaskListSummary {
    taskListId: number;
    serviceId: number;
    serviceName: string;
    processId: number;
    processName: string;
    taskType: number;
    generation: number;
    granularity: number;
    version: number;
    name: string;
    createdDate: string;
    modifiedDate: string;
    taskCount: number;
    snapshotCount: number;
    latestSnapshotId?: number | null;
    latestSnapshotDate?: string | null;
}

export interface AgentTaskListDetailResult {
    isSynthetic: boolean;
    taskList: AgentTaskListSummary;
    latestSnapshot?: AgentTaskSnapshotSummary | null;
    snapshots: AgentTaskSnapshotSummary[];
    pendingWorkItemCount: number;
    completedWorkItemCount: number;
}

export interface AgentTaskSnapshotSummary {
    snapshotId: number;
    taskListId: number;
    environmentId: number;
    metricId?: number | null;
    date: string;
    name?: string | null;
    taskListVersion: number;
    profilePath?: string | null;
    sourceDataPath?: string | null;
}

export interface AgentTaskSnapshotCollectionResult {
    isSynthetic: boolean;
    taskListId: number;
    snapshots: AgentTaskSnapshotSummary[];
}

export interface AgentTaskTaskListResult {
    isSynthetic: boolean;
    taskListId: number;
    snapshotId?: number | null;
    snapshotName?: string | null;
    totalCount: number;
    tasks: AgentTaskTaskSummary[];
}

export interface AgentTaskTaskSummary {
    taskId: number;
    scope: string;
    deleted: boolean;
    flags: number;
    createdDate: string;
    inclusive: number;
    exclusive: number;
    methodCount: number;
    subTaskCount: number;
    depth: number;
    activeProposalCount: number;
    votedProposalCount: number;
    okProposalCount: number;
    hasStructuredAnalysis?: boolean;
    pullRequests: AgentTaskPullRequestSummary[];
    workItems: AgentTaskWorkItemSummary[];
}

export interface AgentTaskTaskDetailResult {
    isSynthetic: boolean;
    taskListId: number;
    taskId: number;
    taskType: number;
    scope: string;
    deleted: boolean;
    flags: number;
    createdDate: string;
    snapshot?: AgentTaskSnapshotSummary | null;
    snapshotData: AgentTaskSnapshotData;
    regressionEvidence?: string | null;
    pullRequests: AgentTaskPullRequestSummary[];
    workItems: AgentTaskWorkItemSummary[];
    extraContext: AgentTaskExtraContextItem[];
}

export interface AgentTaskPullRequestSummary {
    prId: number;
    sourceWorkItemId: number;
    status: number;
    health: number;
    dateLastChecked?: string | null;
    dateCreated?: string | null;
    publishDate?: string | null;
    lastCommitDate?: string | null;
    lastSourceCommitId?: string | null;
}

export interface AgentTaskPullRequestDetail extends AgentTaskPullRequestSummary {
    analysisWorkItemId?: number | null;
    completedCommitId?: string | null;
    autoCompleteObserved?: boolean;
    validationCoordination?: import('../../../../../Contracts/agent-task-manager-typescript/src/api-types.gen').PullRequestValidationCoordinationState | null;
    validationCoordinationError?: string | null;
    repairDiagnostics?: import('../../../../../Contracts/agent-task-manager-typescript/src/api-types.gen').AgentTaskPullRequestRepairDiagnostic[];
    repairProgress?: AgentTaskPullRequestRepairProgress | null;
    taskListId: number;
    taskId: number;
    scope: string;
    signalsObservedAt?: string | null;
    healthInspectionComplete: boolean;
    reviewInspectionComplete: boolean;
    signals: AutoPrSignal[];
    workItems: AgentTaskWorkItemSummary[];
}

export interface AgentTaskSnapshotData {
    inclusive: number;
    exclusive: number;
    methodCount: number;
    subTaskCount: number;
    depth: number;
}

export interface AgentTaskWorkItemSummary {
    workItemId: number;
    snapshotId: number;
    jobName: string;
    attemptNumber?: number | null;
    openQuestionCount?: number;
    answeredQuestionCount?: number;
    declinedQuestionCount?: number;
    proposedChangesPreview?: string | null;
    previousWorkItemId?: number | null;
    repairOfWorkItemId?: number | null;
    repairTriggers?: number | null;
    repairValidationTargetCount?: number;
    repairReviewTargetCount?: number;
    workItemKind: number;
    canCreatePR?: boolean;
    agencyJobId?: string | null;
    adoId?: number | null;
    jobStatus: number;
    pullRequestStatus: number;
    pullRequestHealth?: number | null;
    prId?: number | null;
    latestRepairWorkItem?: AgentTaskWorkItemSummary | null;
    repairWorkItems?: AgentTaskWorkItemSummary[];
    estimatedGain?: number | null;
    agentStartDate?: string | null;
    agentEndDate?: string | null;
    error?: string | null;
    agencyLogsAvailable: boolean;
    agencyLogsArchiveAvailable: boolean;
}

export interface AgencyJobLogs {
    id?: unknown;
    chatHistory: AgencyJobLogMessage[];
    toolDiffs?: unknown[];
    createdAt?: unknown;
    updatedAt?: unknown;
    [key: string]: unknown;
}

export interface AgencyJobLogMessage {
    role?: { label?: string; [key: string]: unknown };
    authorName?: string;
    items?: AgencyJobLogContentItem[];
    [key: string]: unknown;
}

export interface AgencyJobLogContentItem {
    $type?: string;
    text?: unknown;
    id?: unknown;
    callId?: unknown;
    functionName?: unknown;
    arguments?: unknown;
    result?: unknown;
    [key: string]: unknown;
}

export interface AgencyLogsArchiveIndexResult {
    previewLimitBytes: number;
    entries: AgencyLogsArchiveEntry[];
    warnings?: string[];
}

export interface AgencyLogsArchiveEntry {
    path: string;
    name: string;
    directoryPath: string;
    length: number;
    compressedLength: number;
    previewKind: 'json' | 'jsonl' | 'text' | 'binary' | string;
}

export interface AgencyLogsArchiveTreeNode {
    name: string;
    path: string;
    isFile: boolean;
    entry?: AgencyLogsArchiveEntry;
    children: AgencyLogsArchiveTreeNode[];
}

export interface AgentTaskWorkItemDetailResult extends AgentTaskWorkItemSummary {
    taskListId: number;
    taskId: number;
    taskScope: string;
    proposedChanges?: string | null;
    codeChanges?: string | null;
    resultSummary?: string | null;
    assumptions: string[];
    noChangeReason?: string | null;
    expectedReductionPercent?: number | null;
    taskSnapshotInclusivePercent?: number | null;
    expectedSnapshotReductionPercent?: number | null;
    proposedChangeEvents: AgentTaskProposedChangeResult[];
    output1?: string | null;
    output2?: string | null;
    modelUsed?: string | null;
    tokensConsumed?: number | null;
    aiu?: number | null;
    shutdownStats?: string | null;
    resolvedDefinitionJson?: string | null;
    questions: AgentTaskClarificationQuestion[];
}

export type AgentTaskCandidateOutcome = 'proposed' | 'retained' | 'revised' | 'discarded';

export type AgentTaskProposedChangesView = 'current' | 'history';
export type AgentTaskProposedChangesSortKey = 'scope' | 'gainEstimate' | 'lastAnalyzed';
export type AgentTaskSortDirection = 'asc' | 'desc';

export interface AgentTaskPullRequestReference {
    prId: number;
    pullRequestStatus: number;
    pullRequestHealth?: number | null;
}

export interface AgentTaskProposedChangeResultSet {
    taskListId: number;
    taskId: number;
    taskScope: string;
    snapshotId: number;
    taskSnapshotInclusivePercent?: number | null;
    workItemId: number;
    jobName: string;
    attemptNumber?: number | null;
    jobStatus: number;
    pullRequestStatus: number;
    pullRequestHealth?: number | null;
    prId?: number | null;
    agentStartDate?: string | null;
    agentEndDate?: string | null;
    resultSummary?: string | null;
    assumptions: string[];
    noChangeReason?: string | null;
    expectedReductionPercent?: number | null;
    expectedSnapshotReductionPercent?: number | null;
    newerAttemptWorkItemId?: number | null;
    newerAttemptStatus?: number | null;
    pullRequests: AgentTaskPullRequestReference[];
    proposedChangeEvents: AgentTaskProposedChangeResult[];
    questions: AgentTaskQuestion[];
}

export interface AgentTaskProposedChangeCollectionResult {
    resultSets: AgentTaskProposedChangeResultSet[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
}

export interface AgentTaskProposedChangeFilters {
    taskListId?: number;
    taskId?: number;
    workItemId?: number;
    candidateId?: string;
    jobName?: string;
}

export interface AgentTaskProposedChangeResult {
    analysisItemId: number;
    candidateId: string;
    outcome: AgentTaskCandidateOutcome;
    summary: string;
    text: string;
    rationale?: string | null;
    expectedReductionPercent?: number | null;
    analysisRound: number;
    ordinal: number;
    originWorkItemId: number;
    vote: AgentTaskProposalVote | null;
    proposalPrId: number | null;
    rowVersion: string;
    isCurrent: boolean;
    locations: AgentTaskQuestionSourceLocation[];
}

export enum AgentTaskProposalVote {
    Ok = 0,
    Blocked = 1,
    TooSmall = 2,
    Deferred = 3,
}

export interface AgentTaskProposalVoteUpdateResult {
    proposals: AgentTaskProposedChangeResult[];
    activeProposalCount: number;
    votedProposalCount: number;
    okProposalCount: number;
}

export interface AgentTaskClarificationQuestion {
    questionId: number;
    workItemId: number;
    taskListId: number;
    taskId: number;
    snapshotId: number;
    taskScope: string;
    jobName: string;
    attempt: number;
    ordinal: number;
    candidateChangeId?: string | null;
    questionText: string;
    context?: AgentTaskQuestionContext | null;
    state: number;
    answer?: string | null;
    createdDate: string;
    modifiedDate: string;
    resolvedDate?: string | null;
    resolvedBy?: string | null;
    rowVersion: string;
}

export type AgentTaskQuestionView = 'active' | 'history';

export interface AgentTaskQuestionCandidateChange {
    id?: string;
    summary?: string;
}

export interface AgentTaskQuestionSourceLocation {
    repository?: string;
    project?: string;
    path?: string;
    ref?: string;
    startLine?: number;
    endLine?: number;
    symbol?: string;
    snippet?: string;
    codeUrl?: string;
}

export interface AgentTaskQuestionContext {
    schemaVersion?: number;
    decisionImpact?: string;
    candidateChange?: AgentTaskQuestionCandidateChange;
    locations: AgentTaskQuestionSourceLocation[];
}

export interface AgentTaskQuestion {
    questionId: number;
    taskListId: number;
    taskId: number;
    workItemId?: number | null;
    snapshotId?: number | null;
    taskScope: string;
    jobName?: string | null;
    attempt?: number;
    ordinal?: number;
    candidateChangeId?: string | null;
    question: string;
    context?: AgentTaskQuestionContext;
    state?: number;
    answer?: string | null;
    declined: boolean;
    createdDate?: string | null;
    modifiedDate?: string | null;
    resolvedDate?: string | null;
    resolvedBy?: string | null;
    rowVersion?: string | null;
    isResolved: boolean;
}

export interface AgentTaskQuestionPageResult {
    questions: AgentTaskQuestion[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
}

export interface AgentTaskQuestionFilters {
    taskListId?: number;
    taskId?: number;
    workItemId?: number;
}

export interface AgentTaskQuestionResolution {
    questionId: number;
    answer?: string | null;
    declined: boolean;
    rowVersion?: string | null;
}

export interface AgentTaskExtraContextItem {
    contextId: number;
    info: string;
    disabled: boolean;
}

export interface BatchUpdateAgentTaskWorkItemsResult {
    isSynthetic: boolean;
    taskListId: number;
    snapshotId: number;
    pullRequestStatus?: number | null;
    workItems: AgentTaskWorkItemSummary[];
}

export interface AgentTaskDashboardResult {
    isSynthetic: boolean;
    bucket: string;
    items: AgentTaskDashboardItem[];
}

export interface AgentTaskDashboardItem {
    taskListId: number;
    taskListName: string;
    taskType: number;
    taskId: number;
    scope: string;
    snapshotId: number;
    workItemId: number;
    jobName: string;
    agencyJobId?: string | null;
    jobStatus: number;
    pullRequestStatus: number;
    pullRequestHealth?: number | null;
    adoId?: number | null;
    prId?: number | null;
    estimatedGain?: number | null;
    agencyLogsAvailable: boolean;
    agencyLogsArchiveAvailable: boolean;
}

/** Scope-wide lifecycle counts and filter options, independent of the filtered items returned on this page. */
export interface AutoPrDashboardResult {
    stageCounts: {
        draft: number;
        readyToPublish: number;
        published: number;
        blockedOnSignoff: number;
        readyToComplete: number;
        completed: number;
        unclassifiedActive: number;
    };
    view: number;
    totalCount: number;
    activeCount: number;
    completedCount: number;
    draftCount: number;
    readyToPublishCount: number;
    publishedCount: number;
    signoffCompleteCount: number;
    readyToMergeCount: number;
    reviewerGroups: AutoPrReviewerGroupOption[];
    taskLists: AutoPrTaskListOption[];
    items: AutoPrDashboardItem[];
}

export interface AutoPrTaskListOption {
    taskListId: number;
    name: string;
    generation: number;
}

export interface AutoPrReviewerGroupOption {
    signalKey: string;
    displayName: string;
    pendingCount: number;
}

export interface PublishAgentTaskPullRequestsResult {
    requestedCount: number;
    publishedCount: number;
    notPublishedCount: number;
    outcomes: PublishAgentTaskPullRequestOutcome[];
}

export interface PublishAgentTaskPullRequestOutcome {
    taskListId: number;
    taskId: number;
    prId: number;
    scope: string;
    published: boolean;
    code: string;
    message: string;
}

export interface AutoPrDashboardItem {
    repairProgress?: AgentTaskPullRequestRepairProgress | null;
    key?: string | number;
    serviceId: number;
    prId: number;
    taskListId: number;
    taskId: number;
    sourceWorkItemId: number;
    scope: string;
    status: number;
    health: number;
    attentionLevel: number;
    attentionSummary: string;
    dateCreated?: string | null;
    publishDate?: string | null;
    dateLastChecked?: string | null;
    lastCommitDate?: string | null;
    lastSourceCommitId?: string | null;
    estimatedGain?: number | null;
    repairWorkItemId?: number | null;
    repairJobStatus?: number | null;
    mergeCommitId?: string | null;
    completedDate?: string | null;
    deploymentStatus?: number | null;
    measurementEnvironmentId?: number | null;
    measurementEnvironmentName?: string | null;
    measurementStatus?: string | null;
    realizedGain?: number | null;
    variance?: number | null;
    signalsObservedAt?: string | null;
    healthInspectionComplete: boolean;
    reviewInspectionComplete: boolean;
    canResolve: boolean;
    signals: AutoPrSignal[];
}

export interface AutoPrSignal {
    category: number;
    signalKey: string;
    name: string;
    state?: string | null;
    detail?: string | null;
    attentionLevel: number;
    isBlocking?: boolean | null;
    isRequired?: boolean | null;
    isSatisfied?: boolean | null;
    isExpired: boolean;
    isCurrentFailure: boolean;
    requiresIntervention: boolean;
    displayName?: string | null;
    emailAddress?: string | null;
    isIdentityContainer?: boolean | null;
    vote?: number | null;
    threadId?: number | null;
    commentId?: number | null;
    commentVersion?: string | null;
    buildId?: number | null;
    buildDefinitionId?: number | null;
    targetUrl?: string | null;
    updatedDate?: string | null;
}

export interface JobDefinitionCollectionResult {
    serviceId: number;
    jobDefinitions: JobDefinitionSummary[];
}

export interface JobDefinitionSummary {
    serviceId: number;
    name: string;
    taskType: number;
    jobDefinitionId: number;
    disabled: boolean;
    canCreatePR: boolean;
    canPublishPR: boolean;
    executionBackend: number;
    maxConcurrency?: number | null;
    effectiveMaxConcurrency: number;
}

export interface JobModelConfiguration {
    modelId: string;
    reasoningEffort?: string | null;
    useLongContext: boolean;
}

export interface JobDefinitionDetailResult {
    serviceId: number;
    name: string;
    taskType: number;
    jobDefinitionId: number;
    disabled: boolean;
    canCreatePR: boolean;
    canPublishPR: boolean;
    agentArtifactId?: number | null;
    selectedAgentArtifact?: ArtifactSummary | null;
    pipelineArtifactId?: number | null;
    selectedPipelineArtifact?: ArtifactSummary | null;
    dependsOnJobDefinitionId?: number | null;
    dependsOnJobName?: string | null;
    executionBackend: number;
    maxConcurrency?: number | null;
    effectiveMaxConcurrency: number;
    modelConfiguration?: JobModelConfiguration | null;
    text: string;
    relatedArtifacts: ArtifactSummary[];
}

export interface AgencyModelsSettingResult {
    found: boolean;
    value?: string | null;
}

export interface AgencyModelPickerItem {
    id: string;
    name: string;
    reasoningEffortOptions: string[];
}

export interface ArtifactCollectionResult {
    serviceId: number;
    artifacts: ArtifactSummary[];
}

export interface ArtifactSummary {
    id: number;
    serviceId: number;
    name: string;
    type: number;
    text: string;
    disabled: boolean;
}

export interface ArtifactPreviewResult {
    text: string;
}

export interface AgentTaskSummaryResult {
    isSynthetic: boolean;
    taskListCount: number;
    taskCount: number;
    workItemCount: number;
    inProgressJobCount: number;
    failedJobCount: number;
    draftCount: number;
    pendingInternalReviewCount: number;
    publishedCount: number;
    blockedCount: number;
    signedOffCount: number;
    completedCount: number;
    abandonedCount: number;
    tokensConsumed: number;
    averageHoursToSignoff: number;
}

export interface AgentTaskFlameGraphResult {
    isSynthetic: boolean;
    unfilteredTotal: number;
    nodes: AgentTaskFlameGraphNode[];
    error?: string | null;
}

export interface AgentTaskProfileStreamResult {
    isSynthetic: boolean;
    kind: number;
    sasUrl: string;
}

export interface AgentTaskFlameGraphNode {
    name: string;
    value: number;
    exclusive: number;
    id: number;
    parentId: number;
    children?: AgentTaskFlameGraphNode[];
}

export interface ProfileDay {
    dateAsString: string;
}

export interface CreateTaskListFormState {
    name: string;
    processId?: number;
    taskType: number;
    granularityPercent: string;
    environmentId?: number;
    date?: string;
    file?: File | null;
}

export interface CreateSnapshotFormState {
    name: string;
    environmentId?: number;
    date?: string;
    file?: File | null;
}

export interface TaskListCreateConflictResponse {
    message?: string;
    processId?: number;
    taskType?: number;
    matchingCount?: number;
    latestGeneration?: number;
}

export type DefinitionSection = 'jobs' | 'agents' | 'plugins' | 'pipelines' | 'extractions' | 'skills-scripts' | 'snippets';

export type AgencyArtifactResolutionType = 'Repo' | 'Organization' | 'Company' | 'Spec';
export type AgencySpecReferenceFormat = 'market' | 'ado-git' | 'github';
export interface AgencySpecFieldDescriptor {
    key: string;
    label: string;
    optional?: boolean;
}

export interface AgentArtifactInputRow {
    key: string;
    value: string;
}

export interface AgentArtifactEditorState {
    name: string;
    disabled: boolean;
    runtimeName: string;
    resolutionType: AgencyArtifactResolutionType;
    pluginName: string;
    selectedPluginArtifactId?: number;
    unmatchedPluginReference?: string;
    unmatchedPluginResolutionType?: AgencyArtifactResolutionType;
    inputs: AgentArtifactInputRow[];
}

export interface PluginArtifactEditorState {
    name: string;
    disabled: boolean;
    runtimeName: string;
    resolutionType: AgencyArtifactResolutionType;
    specFormat: AgencySpecReferenceFormat;
    specSegments: string[];
}

export interface PipelineArtifactEditorState {
    name: string;
    disabled: boolean;
    pipelineId: string;
    projectId: string;
    pipelineRefOverride: string;
}

export type JobResultExtractionSource = 'LastAssistantText' | 'CombinedAssistantText' | 'AssistantMessages' | 'CombinedToolResultText' | 'FullTranscript';
export type JobResultExtractionMode = 'Regex' | 'Delimited';
export type JobResultExtractionDestination = 'EstimatedGain' | 'Output1' | 'Output2';
export type JobResultExtractionPurpose = 'GenericOutput' | 'CodeChanges' | 'TaskBreakdown' | 'ClarificationQuestions';

export interface ExtractionArtifactEditorState {
    name: string;
    disabled: boolean;
    purpose: JobResultExtractionPurpose;
    source: JobResultExtractionSource;
    mode: JobResultExtractionMode;
    regexPattern: string;
    ignoreCase: boolean;
    multiline: boolean;
    singleline: boolean;
    startDelimiter: string;
    endDelimiter: string;
    destination: JobResultExtractionDestination;
}

export interface JobDefinitionEditorState {
    name: string;
    taskType: number;
    text: string;
    disabled: boolean;
    canCreatePR: boolean;
    canPublishPR: boolean;
    agentArtifactId?: number;
    pipelineArtifactId?: number;
    dependsOnJobDefinitionId?: number;
    executionBackend: number;
    maxConcurrency: string;
    effectiveMaxConcurrency: number;
    modelConfiguration: JobModelConfiguration | null;
    artifactIds: number[];
    extractionArtifactIds: number[];
    pluginArtifactIds: number[];
}

export interface ArtifactEditorState {
    name: string;
    type: number;
    text: string;
    disabled: boolean;
}

export const TASK_TYPES: IDropdownOption[] = [
    { key: 0, text: 'CPU' },
    { key: 1, text: 'Memory (CPR)' },
    { key: 2, text: 'Memory (Dump)' },
    { key: 3, text: 'Memory (Monitored scopes)' },
    { key: 4, text: 'Regression' },
];

export const CREATABLE_TASK_TYPES = TASK_TYPES.filter(option => option.key !== 4);

export const DASHBOARD_BUCKETS: IDropdownOption[] = [
    { key: 'internal', text: 'Needs internal attention' },
    { key: 'external', text: 'Needs external signoff' },
    { key: 'running', text: 'Queued or running' },
];

export const PAGE_SIZE_OPTIONS: IDropdownOption[] = [
    { key: 25, text: '25' },
    { key: 50, text: '50' },
    { key: 100, text: '100' },
];

export const ARTIFACT_TYPE_OPTIONS: IDropdownOption[] = [
    { key: 1, text: 'Skill' },
    { key: 2, text: 'Script' },
];

export const AGENCY_ARTIFACT_RESOLUTION_TYPE_OPTIONS: IDropdownOption[] = [
    { key: 'Company', text: 'Company' },
    { key: 'Organization', text: 'Organization' },
    { key: 'Repo', text: 'Repo' },
    { key: 'Spec', text: 'Spec' },
];

export const AGENCY_SPEC_REFERENCE_FORMAT_OPTIONS: IDropdownOption[] = [
    { key: 'market', text: 'market:' },
    { key: 'ado-git', text: 'ado-git:' },
    { key: 'github', text: 'github:' },
];

export const AGENCY_SPEC_FIELD_DESCRIPTORS: Record<AgencySpecReferenceFormat, AgencySpecFieldDescriptor[]> = {
    market: [
        { key: 'name', label: 'Name' },
        { key: 'location', label: 'Location' },
        { key: 'ref', label: 'Reference', optional: true },
    ],
    'ado-git': [
        { key: 'organization', label: 'Organization' },
        { key: 'project', label: 'Project' },
        { key: 'repository', label: 'Repo' },
        { key: 'path', label: 'Path' },
        { key: 'ref', label: 'Reference', optional: true },
    ],
    github: [
        { key: 'owner', label: 'Owner' },
        { key: 'repository', label: 'Repo' },
        { key: 'path', label: 'Path' },
        { key: 'ref', label: 'Reference', optional: true },
    ],
};

export const JOB_EXECUTION_BACKEND_OPTIONS: IDropdownOption[] = [
    { key: 0, text: 'Agency' },
    { key: 1, text: 'CodeStar' },
];

export const JOB_RESULT_EXTRACTION_SOURCE_OPTIONS: IDropdownOption[] = [
    { key: 'LastAssistantText', text: 'Last agent text' },
    { key: 'CombinedAssistantText', text: 'Combined agent text' },
    { key: 'AssistantMessages', text: 'Assistant messages' },
    { key: 'CombinedToolResultText', text: 'Combined tool result text' },
    { key: 'FullTranscript', text: 'Full transcript' },
];

export const JOB_RESULT_EXTRACTION_MODE_OPTIONS: IDropdownOption[] = [
    { key: 'Regex', text: 'Regex' },
    { key: 'Delimited', text: 'Delimited section' },
];

export const JOB_RESULT_EXTRACTION_PURPOSE_OPTIONS: IDropdownOption[] = [
    { key: 'GenericOutput', text: 'Generic output' },
    { key: 'CodeChanges', text: 'Code changes' },
    { key: 'TaskBreakdown', text: 'Task breakdown' },
    { key: 'ClarificationQuestions', text: 'Clarification questions' },
];

export const JOB_RESULT_EXTRACTION_DESTINATION_OPTIONS: IDropdownOption[] = [
    { key: 'EstimatedGain', text: 'EstimatedGain' },
    { key: 'Output1', text: 'Output1' },
    { key: 'Output2', text: 'Output2' },
];

export interface RuntimeSubstitutionOption {
    token: string;
    description: string;
}

export const JOB_RUNTIME_SUBSTITUTION_OPTIONS: RuntimeSubstitutionOption[] = [
    { token: 'Task.Scope', description: 'Current task scope or method name.' },
    { token: 'Task.Type', description: 'Current task-list type as a string, such as CPU or Memory_Dump.' },
    { token: 'Task.SnapshotId', description: 'Selected snapshot id used for enqueue.' },
    { token: 'Task.Id', description: 'Current task id.' },
    { token: 'Task.TaskListId', description: 'Current task-list id.' },
    { token: 'Task.ExtraContext', description: 'Enabled extra-context rows joined with newlines, or empty when none exist.' },
    { token: 'Task.ProposedChanges', description: 'Current active proposals voted Ok, optionally limited to a task-scoped selection. Enqueue requires every active proposal to be voted.' },
    { token: 'Task.AllProposedChanges', description: 'Every proposal in the latest task analysis set, including discarded proposals and vote metadata.' },
    { token: 'SASUrl.task.json', description: 'SAS URL for the task.json collateral file.' },
    { token: 'SASUrl.callgraph-flat.json', description: 'SAS URL for the flat call graph JSON collateral file.' },
    { token: 'SASUrl.callgraph-nested.json', description: 'SAS URL for the nested call graph JSON collateral file.' },
    { token: 'SASUrl.callers-flat.json', description: 'SAS URL for the flat caller graph JSON collateral file.' },
    { token: 'SASUrl.callers-nested.json', description: 'SAS URL for the nested caller graph JSON collateral file.' },
    { token: 'UpstreamJob.Output', description: 'Latest successful output from the job selected in Depends on.' },
    { token: 'UpstreamJob.ProposedChanges', description: 'Canonical structured active changes from the latest successful analysis dependency, including candidate IDs and source context.' },
    { token: 'PreviousJob.Clarifications', description: 'Clarification answers from the latest prior attempt of the selected job, or None.' },
    { token: 'PreviousJob.LogsSasUrl', description: 'SAS URL for logs from the latest prior attempt of the selected job, or None.' },
];
export const DEFAULT_TASK_LIST_GRANULARITY_PERCENT = '0.2';

export type TaskProfileStreamKind = 'inclusive' | 'exclusive';
export type CallGraphDownloadFormat = 'flat' | 'nested';
export type TaskListSortKey = 'scope' | 'inclusive' | 'exclusive' | 'gainEstimate' | 'methodCount' | 'subTaskCount' | 'depth' | 'jobStatus' | 'pullRequestStatus' | 'workItems';
export type TaskListSortDirection = 'asc' | 'desc';
export type AgentTaskLifecycleStatus =
    | 'notStarted'
    | 'analysisInProgress'
    | 'needsInput'
    | 'analyzed'
    | 'analyzedNoFix'
    | 'readyToCode'
    | 'codingInProgress'
    | 'prInProgress'
    | 'prBlocked'
    | 'done'
    | 'failed'
    | 'other';

export interface AgentTaskListRowModel extends IObjectWithKey {
    key: number;
    task: AgentTaskTaskSummary;
    displayWorkItems: AgentTaskWorkItemSummary[];
    jobSummaryWorkItems: AgentTaskWorkItemSummary[];
    displayPullRequests: AgentTaskPullRequestSummary[];
    displayJobStatuses: number[];
    displayPullRequestStatuses: number[];
    gainEstimatePercent?: number;
    primaryJobStatus?: number;
    primaryPullRequestStatus?: number;
    latestDisplayWorkItemId?: number;
    taskStatus: AgentTaskLifecycleStatus;
}

export const AGENT_TASK_LIFECYCLE_STATUSES: AgentTaskLifecycleStatus[] = [
    'notStarted',
    'analysisInProgress',
    'needsInput',
    'analyzed',
    'analyzedNoFix',
    'readyToCode',
    'codingInProgress',
    'prInProgress',
    'prBlocked',
    'done',
    'failed',
    'other',
];

export interface RepairAgentTaskPullRequestResult {
    action: number;
    workItem?: AgentTaskWorkItemSummary | null;
    requeuedPolicyCount: number;
    requeuedPolicyNames: string[];
    deferredPolicyCount: number;
    deferredPolicyNames: string[];
}

export const TASK_PROFILE_STREAM_KEYS: Record<TaskProfileStreamKind, { requestValue: number; label: string }> = {
    inclusive: {
        requestValue: 0,
        label: 'Inclusive',
    },
    exclusive: {
        requestValue: 1,
        label: 'Exclusive',
    },
};

export const TASK_CALL_GRAPH_DOWNLOAD_LABELS: Record<CallGraphDownloadFormat, string> = {
    flat: 'callgraph-flat.json',
    nested: 'callgraph-nested.json',
};

export const DEFINITION_SECTION_OPTIONS: { key: DefinitionSection; text: string; description: string }[] = [
    { key: 'jobs', text: 'Jobs', description: 'Manage SQL-backed job definitions and their artifact relationships.' },
    { key: 'agents', text: 'Agents', description: 'Edit Agency v2 custom agent definitions stored as JSON-backed artifacts.' },
    { key: 'plugins', text: 'Plugins', description: 'Edit Agency v2 plugin definitions stored as JSON-backed artifacts.' },
    { key: 'pipelines', text: 'Pipelines', description: 'Edit reusable Pipeline compute artifacts for Agency-backed jobs.' },
    { key: 'extractions', text: 'Extractions', description: 'Edit reusable Agency transcript extraction rules stored as JSON-backed artifacts.' },
    { key: 'skills-scripts', text: 'Skills and scripts', description: 'Edit reusable skill and script artifacts for the current service.' },
    { key: 'snippets', text: 'Snippets', description: 'Edit reusable snippet text used by jobs, skills, and scripts.' },
];

export const SNIPPET_SYNTAX_REGEX = /\{\{\s*[^{}]+?\s*\}\}/;
export const TASK_LIST_SORT_KEYS: TaskListSortKey[] = ['scope', 'inclusive', 'exclusive', 'gainEstimate', 'methodCount', 'subTaskCount', 'depth', 'jobStatus', 'pullRequestStatus', 'workItems'];


export interface EnqueueAgentTasksResult {
    isSynthetic: boolean;
    taskListId: number;
    snapshotId: number;
    jobName: string;
    stoppedOnFailure: boolean;
    failedTaskId?: number | null;
    failureMessage?: string | null;
    failureStatusCode?: number | null;
    failureResponseBody?: string | null;
    agencyJobCreationStatus?: AgencyJobCreationStatus | null;
    rejectedTaskCount: number;
    failures: EnqueueAgentTaskFailure[];
    workItems: AgentTaskWorkItemSummary[];
}

export interface EnqueueAgentTasksRequest {
    taskIds: number[];
    jobName: string;
    snapshotId: number;
    modelConfiguration: JobModelConfiguration | null;
    submissionMode?: AgentTaskEnqueueSubmissionMode;
    selectedProposalIds: number[];
}

export enum AgentTaskEnqueueSubmissionMode {
    QueueInternally = 0,
    SubmitImmediately = 1,
}

export interface EnqueueAgentTaskFailure {
    taskId: number;
    scope: string;
    message: string;
    statusCode?: number | null;
    responseBody?: string | null;
}

export interface EnqueueAgentTaskPromptPreviewResult {
    taskListId: number;
    snapshotId: number;
    taskId: number;
    taskScope: string;
    jobName: string;
    prompt: string;
    editablePromptText: string;
    editableRuntimeSubstitutions: PromptRuntimeSubstitutionRange[];
    availableRuntimeSubstitutions: string[];
    containsSasPlaceholders: boolean;
    containsPreviewSasUrls: boolean;
}

export interface PromptRuntimeSubstitutionRange {
    start: number;
    length: number;
}

export enum AgencyJobCreationThrottleType {
    Concurrency = 0,
    Daily = 1,
}

export interface AgencyJobCreationStatus {
    isPaused: boolean;
    throttleType?: AgencyJobCreationThrottleType | null;
    resumeAtUtc?: string | null;
}
